/**
 * @fileoverview Partners Modal component for managing partner information.
 * Provides functionality for adding and managing partner details in the system.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash, faEdit, faSave, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import PartnerOnboardingModal from './PartnerOnboardingModal';
import './PartnersModal.css';

/**
 * @typedef {Object} PartnersModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Callback function to close the modal
 * @property {Function} onPartnerAdded - Callback function called when a new partner is added
 */

/**
 * @typedef {Object} Partner
 * @property {string} name - Partner name
 * @property {string} email - Partner email
 * @property {string} company - Partner company name
 * @property {string} role - Partner role in the system
 */

/**
 * Partners Modal component for managing partner information.
 * Features include:
 * - Adding new partners
 * - Viewing partner details
 * - Managing partner relationships
 * 
 * @param {PartnersModalProps} props - Component props
 * @returns {React.ReactElement|null} The rendered modal or null if not open
 */
const PartnersModal = ({ isOpen, onClose, onPartnerAdded }) => {
  const [newPartner, setNewPartner] = useState('');
  const [partners, setPartners] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    company_name: '',
    partner_email: '',
    partner_phone: '',
    partner_contact_person: '',
    no_direct_client_messages: false,
    agency_contact_email: '',
    agency_contact_phone: '',
    agency_contact_person: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPartners();
    }
  }, [isOpen]);

  /**
   * Fetches the list of existing partners
   * @async
   * @returns {Promise<void>}
   */
  const fetchPartners = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PARTNERS.LIST);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  /**
   * Handles form submission for adding a new partner
   * @async
   * @param {React.FormEvent} e - Form event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(API_ENDPOINTS.PARTNERS.ADD, {
        name: newPartner
      });
      if (onPartnerAdded) {
        onPartnerAdded(response.data);
      }
      setPartners(prev => [...prev, response.data]);
      setNewPartner('');
    } catch (error) {
      console.error('Error adding partner:', error);
    }
  };

  /**
   * Handles partner deletion with confirmation
   * @async
   * @param {string} partnerId - ID of the partner to delete
   * @returns {Promise<void>}
   */
  const handleDelete = async (partnerId) => {
    try {
      const response = await axios.delete(API_ENDPOINTS.PARTNERS.DELETE(partnerId));
      
      if (response.data.status === 'success') {
        // Remove the partner from the local state
        setPartners(prev => prev.filter(partner => partner.id !== partnerId));
        console.log('Partner deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Failed to delete partner. Please try again.');
    } finally {
      setDeleteConfirm(null);
    }
  };

  /**
   * Shows confirmation dialog for partner deletion
   * @param {Object} partner - Partner object to delete
   */
  const confirmDelete = (partner) => {
    setDeleteConfirm(partner);
  };

  /**
   * Handles completion of partner onboarding
   */
  const handleOnboardingComplete = () => {
    // Refresh partners list
    fetchPartners();
    // Call parent callback if provided
    if (onPartnerAdded) {
      onPartnerAdded();
    }
  };
  
  /**
   * Starts editing a partner
   * @param {Object} partner - Partner to edit
   */
  const handleStartEdit = (partner) => {
    setEditingPartner(partner.id);
    setEditForm({
      name: partner.name,
      company_name: partner.company_name || '',
      partner_email: partner.partner_email || '',
      partner_phone: partner.partner_phone || '',
      partner_contact_person: partner.partner_contact_person || '',
      no_direct_client_messages: partner.no_direct_client_messages || false,
      agency_contact_email: partner.agency_contact_email || '',
      agency_contact_phone: partner.agency_contact_phone || '',
      agency_contact_person: partner.agency_contact_person || ''
    });
  };
  
  /**
   * Cancels editing
   */
  const handleCancelEdit = () => {
    setEditingPartner(null);
    setEditForm({
      name: '',
      company_name: '',
      partner_email: '',
      partner_phone: '',
      partner_contact_person: '',
      no_direct_client_messages: false,
      agency_contact_email: '',
      agency_contact_phone: '',
      agency_contact_person: ''
    });
  };
  
  /**
   * Saves partner edits
   * @param {string} partnerId - Partner ID
   */
  const handleSaveEdit = async (partnerId) => {
    try {
      // Validation
      if (!editForm.name.trim()) {
        alert('Partner name is required');
        return;
      }
      
      if (editForm.no_direct_client_messages) {
        if (!editForm.agency_contact_email.trim()) {
          alert('Agency contact email is required when blocking direct client messages');
          return;
        }
        if (!editForm.agency_contact_person.trim()) {
          alert('Agency contact person is required when blocking direct client messages');
          return;
        }
      }
      
      const response = await axios.put(`${API_ENDPOINTS.API_BASE_URL}/api/partners/${partnerId}`, editForm);
      
      if (response.data.status === 'success') {
        // Update local state
        setPartners(prev => prev.map(p => 
          p.id === partnerId ? { ...p, ...editForm } : p
        ));
        handleCancelEdit();
      }
    } catch (error) {
      console.error('Error updating partner:', error);
      alert('Failed to update partner. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="partners-modal-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}>
        <div className="partners-modal" onClick={(e) => e.stopPropagation()}>
          <h2>Manage Partners</h2>
          <button onClick={(e) => {
            e.stopPropagation();
            onClose();
          }} className="close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="partner-setup-buttons">
            <button 
              type="button" 
              className="new-partner-setup-button"
              onClick={() => setShowOnboardingModal(true)}
            >
              + New Partner Setup
            </button>
            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }} className="add-partner-form">
              <input
                type="text"
                value={newPartner}
                onChange={(e) => setNewPartner(e.target.value)}
                placeholder="Quick add partner name"
                required
              />
              <button type="submit" className="add-partner-button">Quick Add</button>
            </form>
          </div>

        <div className="partners-list">
          {partners.map(partner => (
            <div key={partner.id} className="partner-item" style={{ 
              padding: editingPartner === partner.id ? '20px' : '12px',
              backgroundColor: partner.no_direct_client_messages ? '#fff3cd' : 'white'
            }}>
              {editingPartner === partner.id ? (
                // Edit Mode
                <div className="partner-edit-form">
                  <div className="form-group-partners-modal">
                    <label className="form-label-partners-modal">
                      Partner Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="form-input-large-partners-modal"
                    />
                  </div>
                  
                  <div className="form-group-partners-modal">
                    <label className="form-label-partners-modal">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={editForm.company_name}
                      onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                      className="form-input-large-partners-modal"
                      placeholder="Company name for agreements"
                    />
                  </div>
                  
                  <div className="contact-section-partners-modal">
                    <h4 className="contact-header-partners-modal">Partner Contact</h4>
                    
                    <div className="contact-form-group-partners-modal">
                      <label className="contact-form-label-partners-modal">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        value={editForm.partner_contact_person}
                        onChange={(e) => setEditForm({...editForm, partner_contact_person: e.target.value})}
                        className="contact-form-input-partners-modal"
                        placeholder="Contact person name"
                      />
                    </div>
                    
                    <div className="contact-form-group-partners-modal">
                      <label className="contact-form-label-partners-modal">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={editForm.partner_email}
                        onChange={(e) => setEditForm({...editForm, partner_email: e.target.value})}
                        className="contact-form-input-partners-modal"
                        placeholder="contact@partner.com"
                      />
                    </div>
                    
                    <div className="contact-form-group-partners-modal">
                      <label className="contact-form-label-partners-modal">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={editForm.partner_phone}
                        onChange={(e) => setEditForm({...editForm, partner_phone: e.target.value})}
                        className="contact-form-input-partners-modal"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group-partners-modal">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editForm.no_direct_client_messages}
                        onChange={(e) => setEditForm({...editForm, no_direct_client_messages: e.target.checked})}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>No direct client messages</span>
                    </label>
                  </div>
                  
                  {editForm.no_direct_client_messages && (
                    <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '15px' }}>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                          Contact Person *
                        </label>
                        <input
                          type="text"
                          value={editForm.agency_contact_person}
                          onChange={(e) => setEditForm({...editForm, agency_contact_person: e.target.value})}
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                        />
                      </div>
                      
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                          Contact Email *
                        </label>
                        <input
                          type="email"
                          value={editForm.agency_contact_email}
                          onChange={(e) => setEditForm({...editForm, agency_contact_email: e.target.value})}
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                          Contact Phone
                        </label>
                        <input
                          type="tel"
                          value={editForm.agency_contact_phone}
                          onChange={(e) => setEditForm({...editForm, agency_contact_phone: e.target.value})}
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={handleCancelEdit}
                      style={{ 
                        padding: '6px 12px', 
                        borderRadius: '4px', 
                        border: '1px solid #ddd', 
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleSaveEdit(partner.id)}
                      style={{ 
                        padding: '6px 12px', 
                        borderRadius: '4px', 
                        border: 'none', 
                        backgroundColor: '#007bff', 
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <FontAwesomeIcon icon={faSave} /> Save
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="partner-name">{partner.name}</span>
                      {partner.no_direct_client_messages && (
                        <span style={{ 
                          fontSize: '11px', 
                          padding: '2px 8px', 
                          backgroundColor: '#856404', 
                          color: 'white', 
                          borderRadius: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '10px' }} />
                          Agency Managed
                        </span>
                      )}
                    </div>
                    {partner.company_name && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        Company: {partner.company_name}
                      </div>
                    )}
                    {(partner.partner_email || partner.partner_contact_person) && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        Contact: {partner.partner_contact_person || 'N/A'} {partner.partner_email && `(${partner.partner_email})`}
                      </div>
                    )}
                    {partner.no_direct_client_messages && partner.agency_contact_person && (
                      <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px', fontStyle: 'italic' }}>
                        Agency: {partner.agency_contact_person} ({partner.agency_contact_email})
                      </div>
                    )}
                  </div>
                  <div className="partner-actions">
                    <button 
                      className="edit-button" 
                      title="Edit"
                      onClick={() => handleStartEdit(partner)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="delete-button" 
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(partner);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <div className="delete-confirm-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete partner "{deleteConfirm.name}"? 
                This will also remove them from any client relationships.
              </p>
              <div className="delete-confirm-buttons">
                <button 
                  className="cancel-button"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-delete-button"
                  onClick={() => handleDelete(deleteConfirm.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Partner Onboarding Modal */}
      <PartnerOnboardingModal
        isOpen={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        onComplete={handleOnboardingComplete}
      />
    </>
  );
};

export default PartnersModal; 